import { useMutation, useQuery, type UseQueryResult } from "@tanstack/react-query";
import { getSupabaseBrowserClient } from "../integrations/supabase/client";
import { refuseInView, sourcedKey, useReadSource, viewRows } from "./readSource";

/**
 * The document catalogue, and the two-step download behind it.
 *
 * The catalogue is an RPC rather than a table read because everything worth showing lives in
 * `private`: version label, hash, size and media type are on `private.document_versions` and
 * `private.storage_objects`, which no browser role holds any privilege on. `schema.md` section 11
 * says why the earlier `v_client_documents` view could not do this job.
 *
 * The download is two calls to the Worker rather than a link. A ticket is issued, then spent, and
 * both requests carry the session's access token, so the file cannot be fetched by navigating to a
 * URL. That is the whole point of `architecture.md` section 9: the browser never receives a bucket
 * name, an object key or a reusable URL, and the ticket it does receive is single-use and lives
 * tens of seconds.
 */

export const documentCatalogueKey = (tenantId: string) =>
  ["portal", "documents", tenantId] as const;

export interface CatalogueEntry {
  id: string;
  document_id: string;
  document_version_id: string;
  supersedes_version_id: string | null;
  /** True when nothing else in this same answer supersedes the entry. */
  is_current: boolean;
  display_title: string;
  display_category: string;
  version_label: string;
  sha256: string;
  byte_size: number;
  media_type: string;
  original_filename: string;
  frozen_at: string;
  effective_from: string | null;
  effective_to: string | null;
  visible_from: string;
}

export function useDocumentCatalogue(
  tenantId: string | null
): UseQueryResult<CatalogueEntry[]> {
  const source = useReadSource();
  return useQuery({
    queryKey: sourcedKey(source, documentCatalogueKey(tenantId ?? "none")),
    enabled: Boolean(tenantId),
    queryFn: async () => {
      if (source.kind === "admin") {
        return viewRows<CatalogueEntry>("admin_as_client_document_catalogue", { p_tenant_id: source.tenantId });
      }
      const supabase = getSupabaseBrowserClient();
      const { data, error } = await supabase.rpc("client_document_catalogue", {
        p_tenant_id: tenantId as string
      });

      if (error) {
        throw error;
      }
      return (data ?? []) as CatalogueEntry[];
    }
  });
}

interface TicketResponse {
  ticket: string;
  expires_at: string;
  download_path: string;
}

/**
 * The Worker is same-origin.
 *
 * `architecture.md` section 6 has the Worker serve both `/api/*` and the application itself through
 * its ASSETS binding, so a relative path is the whole address and there is no base URL to configure
 * or to get wrong. Against the Vite dev server there is no Worker behind `/api`, which is why the
 * browser suite intercepts these two calls and the file flow itself is proven end to end against
 * real bytes in `tests/worker/document-flow.test.ts`.
 */
async function callWorker(path: string, init: RequestInit): Promise<Response> {
  const supabase = getSupabaseBrowserClient();
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;

  if (!token) {
    throw new Error("PORTAL_UNAUTHENTICATED");
  }

  const response = await fetch(path, {
    ...init,
    headers: { ...(init.headers ?? {}), Authorization: `Bearer ${token}` }
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(body?.error ?? `PORTAL_DOWNLOAD_FAILED_${response.status}`);
  }

  return response;
}

export interface DownloadRequest {
  tenantId: string;
  documentVersionId: string;
  filename: string;
}

/**
 * Ticket, then bytes, then a save.
 *
 * The second request has to be a `fetch` rather than a navigation, because the Worker authenticates
 * it from the `Authorization` header and a browser navigation carries no header. So the file
 * arrives as a blob and is handed to the person through an object URL, which is revoked
 * immediately: the URL is a handle to memory in this tab, not a second way to reach the file.
 */
export function useDocumentDownload() {
  const source = useReadSource();
  return useMutation({
    mutationFn: async (input: DownloadRequest) => {
      refuseInView(source);
      const ticketResponse = await callWorker("/api/files/document-ticket", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          tenant_id: input.tenantId,
          document_version_id: input.documentVersionId
        })
      });

      const ticket = (await ticketResponse.json()) as TicketResponse;
      const fileResponse = await callWorker(ticket.download_path, { method: "GET" });
      const blob = await fileResponse.blob();

      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = input.filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);

      return input.documentVersionId;
    }
  });
}

export const CATEGORY_LABEL: Record<string, string> = {
  agreement: "Uppdrag",
  terms: "Allmänna villkor",
  processor_agreement: "Personuppgiftsbiträdesavtal",
  scope: "Omfattning",
  change_order: "Ändringsbeställningar",
  handover: "Överlämning",
  other: "Övrigt"
};

/** Agreement first, because it is what the rest hangs from. */
export const CATEGORY_ORDER = [
  "agreement",
  "terms",
  "processor_agreement",
  "scope",
  "change_order",
  "handover",
  "other"
];

export const CATEGORY_NOTE: Record<string, string> = {
  change_order:
    "Godkännande av en ändringsbeställning sker i mejlet, precis som för uppdraget. Portalen visar den godkända versionen.",
  terms:
    "Den version ert uppdrag hänvisar till gäller tills ni fått besked om en ny. Tidigare versioner ligger kvar."
};

export interface DocumentThread {
  documentId: string;
  current: CatalogueEntry;
  historical: CatalogueEntry[];
}

export interface CategoryGroup {
  category: string;
  threads: DocumentThread[];
}

/**
 * The catalogue as the screen shows it: categories, then one thread per document.
 *
 * `document_id` is the group key rather than the supersession chain, because the chain is a
 * relationship between two rows and the group is a fact about all of them. A thread with no current
 * entry cannot normally happen — `is_current` is true unless something in the same answer supersedes
 * it — but the first entry stands in if it ever does, because a document that renders nothing is
 * worse than one that renders its oldest version.
 */
export function groupCatalogue(entries: CatalogueEntry[]): CategoryGroup[] {
  const byCategory = new Map<string, CatalogueEntry[]>();

  for (const entry of entries) {
    const bucket = byCategory.get(entry.display_category);
    if (bucket) {
      bucket.push(entry);
    } else {
      byCategory.set(entry.display_category, [entry]);
    }
  }

  const known = CATEGORY_ORDER.filter((category) => byCategory.has(category));
  const unknown = [...byCategory.keys()].filter((category) => !CATEGORY_ORDER.includes(category));

  return [...known, ...unknown].map((category) => {
    const inCategory = byCategory.get(category) ?? [];
    const documentIds = [...new Set(inCategory.map((entry) => entry.document_id))];

    const threads = documentIds.map((documentId) => {
      const versions = inCategory.filter((entry) => entry.document_id === documentId);
      const current = versions.find((entry) => entry.is_current) ?? versions[0];

      return {
        documentId,
        current,
        historical: versions.filter((entry) => entry !== current)
      };
    });

    return { category, threads };
  });
}
